import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { Search, Plus, MapPin, Calendar, Phone, MessageCircle, CheckCircle, Trash2 } from 'lucide-react';
import './LostFound.css';

export default function LostFound() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [posts,      setPosts]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [tab,        setTab]        = useState('all');
  const [search,     setSearch]     = useState('');
  const [showForm,   setShowForm]   = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    type: 'lost', title: '', description: '', location: '', date: '',
  });

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tab !== 'all')     params.set('type', tab);
      if (search.trim())     params.set('search', search.trim());
      const { data } = await api.get('/lostfound?' + params.toString());
      setPosts(data.posts);
    } catch {
      toast.error('Could not load posts.');
    } finally {
      setLoading(false);
    }
  }, [tab, search]);

  useEffect(() => {
    const t = setTimeout(fetchPosts, 300);
    return () => clearTimeout(t);
  }, [fetchPosts]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description || !form.location || !form.date) {
      toast.error('Please fill all fields.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post('/lostfound', form);
      setPosts(prev => [data.post, ...prev]);
      setShowForm(false);
      setForm({ type: 'lost', title: '', description: '', location: '', date: '' });
      toast.success('Post created!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to post.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResolve = async (id) => {
    try {
      await api.put('/lostfound/' + id + '/resolve');
      setPosts(prev => prev.filter(p => p._id !== id));
      toast.success('Marked as resolved!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await api.delete('/lostfound/' + id);
      setPosts(prev => prev.filter(p => p._id !== id));
      toast.success('Post deleted.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed.');
    }
  };

  return (
    <div className="lf-page fade-in">

      {/* Header */}
      <div className="lf-header">
        <div>
          <h2>Lost & Found</h2>
          <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: 4 }}>
            Lost something on campus? Found something? Post here.
          </p>
        </div>
        {user && (
          <button className="btn btn-primary" onClick={() => setShowForm(s => !s)}>
            <Plus size={16} />
            {showForm ? 'Cancel' : 'Post'}
          </button>
        )}
      </div>

      {/* Post form */}
      {showForm && (
        <div className="lf-form-card card fade-in">
          <h4 style={{ marginBottom: 'var(--space-4)' }}>Create a Post</h4>
          <form onSubmit={handleSubmit} className="lf-form">

            {/* Lost / Found toggle */}
            <div className="lf-type-toggle">
              <button
                type="button"
                className={'lf-type-btn' + (form.type === 'lost' ? ' active-lost' : '')}
                onClick={() => setForm(f => ({ ...f, type: 'lost' }))}
              >
                😟 I Lost Something
              </button>
              <button
                type="button"
                className={'lf-type-btn' + (form.type === 'found' ? ' active-found' : '')}
                onClick={() => setForm(f => ({ ...f, type: 'found' }))}
              >
                🎉 I Found Something
              </button>
            </div>

            <div className="lf-form-grid">
              <div className="form-group">
                <label className="label">Title</label>
                <input className="input" placeholder="e.g. Lost black wallet near library"
                  value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  maxLength={100} />
              </div>

              <div className="form-group">
                <label className="label">Location</label>
                <input className="input" placeholder="e.g. Near library gate, Block B canteen"
                  value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} />
              </div>

              <div className="form-group">
                <label className="label">Date</label>
                <input className="input" type="date"
                  value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
              </div>

              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="label">Description</label>
                <textarea className="textarea"
                  placeholder="Describe the item in detail — color, brand, what's inside..."
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={3} maxLength={500} />
                <span className="helper-text">{form.description.length}/500</span>
              </div>
            </div>

            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting
                ? <><div className="spinner" /> Posting...</>
                : <><Plus size={15} /> Post</>
              }
            </button>
          </form>
        </div>
      )}

      {/* Tabs + Search */}
      <div className="lf-controls">
        <div className="lf-tabs">
          {['all', 'lost', 'found'].map(t => (
            <button key={t}
              className={'lf-tab' + (tab === t ? ' active' : '')}
              onClick={() => { setTab(t); }}>
              {t === 'all' ? 'All Posts' : t === 'lost' ? '😟 Lost' : '🎉 Found'}
            </button>
          ))}
        </div>

        <div className="market-search-wrap" style={{ flex: 1, maxWidth: 300 }}>
          <Search size={15} className="market-search-icon" />
          <input className="market-search" placeholder="Search posts..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      {/* Posts list */}
      {loading ? (
        <div className="loading-center"><div className="spinner spinner-lg" /></div>
      ) : posts.length === 0 ? (
        <div className="empty-state card" style={{ padding: 'var(--space-10)' }}>
          <div className="empty-state-icon">🔍</div>
          <h4>No posts yet</h4>
          <p className="text-muted" style={{ fontSize: '0.875rem' }}>
            Be the first to post a lost or found item
          </p>
        </div>
      ) : (
        <div className="lf-grid">
          {posts.map(post => (
            <PostCard
              key={post._id}
              post={post}
              currentUserId={user ? user._id : null}
              onResolve={handleResolve}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PostCard({ post, currentUserId, onResolve, onDelete }) {
  const isOwn = currentUserId && (
    post.poster === currentUserId ||
    post.poster?._id === currentUserId
  );

  const waLink   = 'https://wa.me/91' + (post.posterSnapshot?.whatsappNumber || '');
  const callLink = 'tel:+91'          + (post.posterSnapshot?.callNumber || '');

  return (
    <div className={'lf-card card ' + (post.type === 'lost' ? 'lf-card-lost' : 'lf-card-found')}>

      {/* Header */}
      <div className="lf-card-header">
        <span className={'lf-badge ' + (post.type === 'lost' ? 'lf-badge-lost' : 'lf-badge-found')}>
          {post.type === 'lost' ? '😟 Lost' : '🎉 Found'}
        </span>
        <span className="text-muted" style={{ fontSize: '0.72rem', flexShrink: 0 }}>
          {new Date(post.createdAt).toLocaleDateString('en-IN', {
            day: 'numeric', month: 'short', year: 'numeric',
          })}
        </span>
      </div>

      {/* Title */}
      <h4 className="lf-card-title">{post.title}</h4>

      {/* Description */}
      <p className="lf-card-desc">{post.description}</p>

      {/* Meta */}
      <div className="lf-card-meta">
        <span className="lf-meta-item">
          <MapPin size={12} />
          <span>{post.location}</span>
        </span>
        <span className="lf-meta-item">
          <Calendar size={12} />
          <span>
            {new Date(post.date).toLocaleDateString('en-IN', {
              day: 'numeric', month: 'short',
            })}
          </span>
        </span>
      </div>

      {/* Poster */}
      <div className="lf-card-poster">
        {post.posterSnapshot?.avatar
          ? <img src={post.posterSnapshot.avatar} alt=""
              style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
          : <div className="avatar-placeholder"
              style={{ width: 28, height: 28, fontSize: '0.7rem', flexShrink: 0 }}>
              {post.posterSnapshot?.name?.[0] || '?'}
            </div>
        }
        <div>
          <span className="lf-poster-name">
            {post.posterSnapshot?.name || 'Unknown'}
          </span>
          <span className="lf-poster-roll">
            {post.posterSnapshot?.rollNumber || ''}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="lf-card-actions">
        {!isOwn && (
          <>
            <a href={waLink} target="_blank" rel="noreferrer" className="btn btn-success btn-sm">
              <MessageCircle size={13} /> WhatsApp
            </a>
            <a href={callLink} className="btn btn-secondary btn-sm">
              <Phone size={13} /> Call
            </a>
          </>
        )}
        {isOwn && (
          <>
            <button className="btn btn-success btn-sm" onClick={() => onResolve(post._id)}>
              <CheckCircle size={13} /> Resolved
            </button>
            <button className="btn btn-danger btn-sm" onClick={() => onDelete(post._id)}>
              <Trash2 size={13} /> Delete
            </button>
          </>
        )}
      </div>

    </div>
  );
}